import React, { useState, useRef } from 'react'
import { Link } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { sendSourceApplication } from './sourceApplicationActions'
import Button from '../../components/ui/Button'
import { validateURL, validateEmail } from '../../user/validate'
import './infoPageForms.pcss'

/** @type {Object<string, (value: string) => boolean>} Field name → validation function */
const FIELD_VALIDATORS = {
  medianame: (value) => value.trim().length >= 2,
  contact: (value) => value.trim().length >= 2,
  email: (value) => validateEmail(value.trim()),
  founder: (value) => value.trim().length >= 2,
  foundationYear: (value) => /^\d{4}$/.test(value.trim()),
  journalists: (value) => /^([1-9][0-9]{0,2})$/.test(value.trim()),
  newsPerDay: (value) => /^([1-9][0-9]{0,2})$/.test(value.trim()),
  contentInfo: (value) => value.trim().length >= 2,
  rssfeed: (value) => validateURL(value.trim()),
  category: (value) => value.trim().length >= 2, // Our shortest category name is "IT"
  paywall: () => true,
  paywallKeyword: (value) => value.trim().length >= 2,
  native: () => true,
  nativeKeyword: (value) => value.trim().length >= 2,
  terms: () => true,
}

const FIELD_NAMES = Object.keys(FIELD_VALIDATORS)

const initialFields = {
  ...Object.fromEntries(FIELD_NAMES.map(name => [name + 'Value', ''])),
  ...Object.fromEntries(FIELD_NAMES.map(name => [name + 'Valid', null])),
}

const SourceApplicationForm = () => {
  const dispatch = useDispatch()
  const loading = useSelector(state => state.sourceApplication.get('loading'))
  const sent = useSelector(state => state.sourceApplication.get('sent'))
  const error = useSelector(state => state.sourceApplication.get('error'))

  const [fields, setFields] = useState(initialFields)
  const refForm = useRef(null)

  const handleInput = (fieldName, e) => {
    const value = e.target.value
    const valid = FIELD_VALIDATORS[fieldName](value) || (e.type === 'blur' ? false : null)
    setFields(prev => ({
      ...prev,
      [fieldName + 'Value']: value,
      [fieldName + 'Valid']: valid,
    }))
  }

  const handlePaywallInput = (e) => {
    const value = e.target.value
    setFields(prev => ({
      ...prev,
      paywallValue: value,
      paywallValid: true,
      paywallKeywordValid: value === 'ei' || prev.paywallKeywordValue.trim().length >= 2 || null,
    }))
  }

  const handleNativeInput = (e) => {
    const value = e.target.value
    setFields(prev => ({
      ...prev,
      nativeValue: value,
      nativeValid: true,
      nativeKeywordValid: value === 'ei' || prev.nativeKeywordValue.trim().length >= 2 || null,
    }))
  }

  const handleTermsInput = (e) => {
    setFields(prev => ({
      ...prev,
      termsValue: e.target.checked ? e.target.value : null,
      termsValid: !!e.target.checked,
    }))
  }

  /**
   * Serializes form to Object { 'Input Name 1': inputValue, 'Input Name 2': inputValue, ... }
   */
  const serializeForm = () => {
    const data = {}
    const form = refForm.current
    for (const input of form) {
      if (input.type === 'radio') {
        data[input.name] = input.checked ? input.value : (data[input.name] ?? '')
      } else if (input.type === 'checkbox') {
        data[input.name] = input.checked ? input.value : '-'
      } else if (input.type !== 'submit') {
        data[input.name] = input.value.trim()
      }
    }
    return data
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const validated = {}
    FIELD_NAMES.forEach(name => {
      validated[name + 'Valid'] = !!fields[name + 'Valid']
    })
    setFields(prev => ({ ...prev, ...validated }))

    const allValid = Object.values(validated).every(v => v === true)
    if (!allValid || loading) {
      return
    }

    dispatch(sendSourceApplication(fields.emailValue.trim(), serializeForm()))
  }

  if (sent) {
    return (
      <section className='info-page-form-thanks'>
        <h2>Kiitos hakemuksestasi!</h2>
      </section>
    )
  }

  if (error) {
    return (
      <section className='info-page-form-error'>
        <h2>Virhe!</h2>
        <p>
          Lomakkeen lähetys epäonnistui. Yritä uudelleen myöhemmin.
        </p>
      </section>
    )
  }

  return (
    <form className='info-page-form' noValidate ref={ refForm } onSubmit={ handleSubmit }>
      <div className='form-group-container'>
        <div className='form-group-label'>
          <label htmlFor='medianame'>Median nimi</label>
        </div>
        <div className='form-group-inputs'>
          <input
            type='text'
            id='medianame'
            name='Median nimi'
            value={ fields.medianameValue }
            onChange={ (e) => handleInput('medianame', e) }
            onInput={ (e) => handleInput('medianame', e) }
            onBlur={ (e) => handleInput('medianame', e) }
          />
          { fields.medianameValid === false && (
            <div className='form-validation-message'>Anna median nimi</div>
          ) }
        </div>
      </div>

      <div className='form-group-container'>
        <div className='form-group-label'>
          <label htmlFor='contact'>Yhteyshenkilön nimi</label>
        </div>
        <div className='form-group-inputs'>
          <input
            type='text'
            id='contact'
            name='Yhteyshenkilön nimi'
            value={ fields.contactValue }
            onChange={ (e) => handleInput('contact', e) }
            onInput={ (e) => handleInput('contact', e) }
            onBlur={ (e) => handleInput('contact', e) }
          />
          { fields.contactValid === false && (
            <div className='form-validation-message'>Anna yhteyshenkilön nimi</div>
          ) }
        </div>
      </div>

      <div className='form-group-container'>
        <div className='form-group-label'>
          <label htmlFor='email'>Yhteyshenkilön sähköposti</label>
        </div>
        <div className='form-group-inputs'>
          <input
            type='email'
            id='email'
            name='Yhteyshenkilön sähköposti'
            value={ fields.emailValue }
            onChange={ (e) => handleInput('email', e) }
            onInput={ (e) => handleInput('email', e) }
            onBlur={ (e) => handleInput('email', e) }
            placeholder='matti.meikalainen@esimerkkimedia.fi'
          />
          { fields.emailValid === false && (
            <div className='form-validation-message'>Anna yhteyshenkilön sähköpostiosoite</div>
          ) }
        </div>
      </div>

      <div className='form-group-container'>
        <div className='form-group-label'>
          <label htmlFor='founder'>Perustaja</label>
        </div>
        <div className='form-group-inputs'>
          <input
            type='text'
            id='founder'
            name='Perustaja'
            value={ fields.founderValue }
            onChange={ (e) => handleInput('founder', e) }
            onInput={ (e) => handleInput('founder', e) }
            onBlur={ (e) => handleInput('founder', e) }
          />
          { fields.founderValid === false && (
            <div className='form-validation-message'>Anna perustajan nimi</div>
          ) }
        </div>
      </div>

      <div className='form-group-container'>
        <div className='form-group-label'>
          <label htmlFor='foundationYear'>Perustamisvuosi</label>
        </div>
        <div className='form-group-inputs'>
          <input
            type='number'
            min='1900'
            id='foundationYear'
            name='Perustamisvuosi'
            value={ fields.foundationYearValue }
            onChange={ (e) => handleInput('foundationYear', e) }
            onInput={ (e) => handleInput('foundationYear', e) }
            onBlur={ (e) => handleInput('foundationYear', e) }
          />
          { fields.foundationYearValid === false && (
            <div className='form-validation-message'>Anna perustamisvuosi</div>
          ) }
        </div>
      </div>

      <div className='form-group-container'>
        <div className='form-group-label'>
          <label htmlFor='journalists'>Toimittajien määrä</label>
        </div>
        <div className='form-group-inputs'>
          <input
            type='number'
            min='0'
            id='journalists'
            name='Toimittajien määrä'
            value={ fields.journalistsValue }
            onChange={ (e) => handleInput('journalists', e) }
            onInput={ (e) => handleInput('journalists', e) }
            onBlur={ (e) => handleInput('journalists', e) }
          />
          { fields.journalistsValid === false && (
            <div className='form-validation-message'>Anna toimittajien määrä</div>
          ) }
        </div>
      </div>

      <div className='form-group-container'>
        <div className='form-group-label'>
          <label htmlFor='newsPerDay'>Montako uutista / päivä (arvio)</label>
        </div>
        <div className='form-group-inputs'>
          <input
            type='number'
            min='0'
            id='newsPerDay'
            name='Montako uutista / päivä (arvio)'
            value={ fields.newsPerDayValue }
            onChange={ (e) => handleInput('newsPerDay', e) }
            onInput={ (e) => handleInput('newsPerDay', e) }
            onBlur={ (e) => handleInput('newsPerDay', e) }
          />
          { fields.newsPerDayValid === false && (
            <div className='form-validation-message'>Anna arvio uutisten määrästä</div>
          ) }
        </div>
      </div>

      <div className='form-group-container'>
        <div className='form-group-label'>
          <label htmlFor='contentInfo'>Kuvailkaa uutissisältöänne</label>
        </div>
        <div className='form-group-inputs'>
          <input
            type='text'
            id='contentInfo'
            name='Kuvailkaa uutissisältöänne'
            value={ fields.contentInfoValue }
            onChange={ (e) => handleInput('contentInfo', e) }
            onInput={ (e) => handleInput('contentInfo', e) }
            onBlur={ (e) => handleInput('contentInfo', e) }
            placeholder='Uutisia, käännösjuttuja, näkökulmia...'
          />
          { fields.contentInfoValid === false && (
            <div className='form-validation-message'>Anna infoteksti</div>
          ) }
        </div>
      </div>

      <div className='form-group-container'>
        <div className='form-group-label'>
          <label htmlFor='rssfeed'>RSS-syötteen osoite</label>
        </div>
        <div className='form-group-inputs'>
          <input
            type='url'
            id='rssfeed'
            name='RSS-syötteen osoite'
            value={ fields.rssfeedValue }
            onChange={ (e) => handleInput('rssfeed', e) }
            onInput={ (e) => handleInput('rssfeed', e) }
            onBlur={ (e) => handleInput('rssfeed', e) }
            placeholder='https://www.esimerkkimedia.fi/feed/'
          />

          { fields.rssfeedValid === false && (
            <div className='form-validation-message'>Anna RSS-syötteen osoite</div>
          ) }
        </div>
      </div>

      <div className='form-group-container'>
        <div className='form-group-label'>
          <label htmlFor='category'>Kategoriaehdotukset</label>
        </div>
        <div className='form-group-inputs'>
          <input
            type='text'
            id='category'
            name='Kategoriaehdotukset'
            value={ fields.categoryValue }
            onChange={ (e) => handleInput('category', e) }
            onInput={ (e) => handleInput('category', e) }
            onBlur={ (e) => handleInput('category', e) }
            placeholder='kotimaa, helsinki, urheilu'
          />
          { fields.categoryValid === false && (
            <div className='form-validation-message'>Anna ehdotus lähteen kategorioiksi</div>
          ) }
        </div>
      </div>

      <div className='form-group-container'>
        <div className='form-group-label'>
          Maksullinen sisältö
        </div>
        <div className='form-group-inputs'>
          <label>
            <input
              type='radio'
              name='paywall'
              value='ei'
              checked={ fields.paywallValue === 'ei' }
              onChange={ handlePaywallInput }
            /> Syötteessä ei ole maksullista sisältöä
          </label>
          <label>
            <input
              type='radio'
              name='paywall'
              value='kyllä'
              checked={ fields.paywallValue === 'kyllä' }
              onChange={ handlePaywallInput }
            /> Maksullinen sisältö on merkitty avainsanalla
          </label>
          { fields.paywallValid === false && (
            <div className='form-validation-message'>Valitse jompi kumpi vaihtoehto</div>
          ) }
          { ( fields.paywallValue === 'kyllä' &&
            <input
              type='text'
              name='Maksullisten juttujen avainsana'
              aria-label='Maksullisten juttujen avainsana'
              placeholder='Maksullisten avainsana'
              value={ fields.paywallKeywordValue }
              onChange={ (e) => handleInput('paywallKeyword', e) }
            />
          ) }
          { fields.paywallValid && fields.paywallKeywordValid === false && (
            <div className='form-validation-message'>Anna maksullisen sisällön avainsana</div>
          ) }
        </div>
      </div>

      <div className='form-group-container'>
        <div className='form-group-label'>
          Kaupallinen sisältö
        </div>
        <div className='form-group-inputs'>
          <label>
            <input
              type='radio'
              name='native'
              value='ei'
              checked={ fields.nativeValue === 'ei' }
              onChange={ handleNativeInput }
            /> Syötteessä ei ole kaupallista sisältöä
          </label>
          <label>
            <input
              type='radio'
              name='native'
              value='kyllä'
              checked={ fields.nativeValue === 'kyllä' }
              onChange={ handleNativeInput }
            /> Kaupallinen sisältö on merkitty avainsanalla
          </label>
          { fields.nativeValid === false && (
            <div className='form-validation-message'>Valitse jompi kumpi vaihtoehto</div>
          ) }
          { ( fields.nativeValue === 'kyllä' &&
            <input
              type='text'
              name='Kaupallisten juttujen avainsana'
              aria-label='Kaupallisten juttujen avainsana'
              placeholder='Kaupallisten avainsana'
              value={ fields.nativeKeywordValue }
              onChange={ (e) => handleInput('nativeKeyword', e) }
            />
          ) }
          { fields.nativeValid && fields.nativeKeywordValid === false && (
            <div className='form-validation-message'>Anna kaupallisen sisällön avainsana</div>
          ) }
        </div>
      </div>

      <div className='form-group-container'>
        <div className='form-group-label'>
        </div>
        <div className='form-group-inputs'>
          <label>
            <input
              type='checkbox'
              name='Hyväksyn ehdot'
              value='kyllä'
              checked={ fields.termsValue === 'kyllä' }
              onChange={ handleTermsInput }
            />  Olen
            lukenut <Link to='/tietoa/lahteille' target='_blank'>lähteiden ohjesivun</Link> ja
            hyväksyn <Link to='/tietoa/ehdot-lahteille' target='_blank'>käyttöehdot</Link>
          </label>
          { fields.termsValid === false && (
            <div className='form-validation-message'>Hyväksy ehdot voidaksesi lähettää hakemuksen</div>
          ) }
        </div>
      </div>

      <div className='form-group-container'>
        <div className='form-group-label'>
        </div>
        <div className='form-group-inputs'>
          <Button
            type='submit'
            text='Lähetä hakemus'
            loading={ loading }
          />
        </div>
      </div>
    </form>
  )
}

export default SourceApplicationForm
